/**
 * usePWAInstall — PWA install prompt detection and cooldown management.
 *
 * Per `.claude/rules/pwa-patterns.md`:
 *   - Captures `beforeinstallprompt` (Chrome/Edge/Samsung).
 *   - Detects iOS Safari (no event, needs manual "Add to Home Screen" guide).
 *   - Respects dismissal cooldown: MAX_DISMISSALS then COOLDOWN_DAYS pause.
 *   - `isInstalled`: true when running in `standalone` display mode.
 *
 * Usage:
 *   const { canInstall, showIOSGuide, prompt, dismiss, isInstalled } = usePWAInstall();
 *   if ((canInstall || showIOSGuide) && !isInstalled && valueMomentReached) {
 *     // show install UI
 *   }
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  INSTALL_COOLDOWN_DAYS,
  INSTALL_MAX_DISMISSALS,
  PWA_STORAGE_KEYS,
} from "#/lib/pwa/config.js";

// ---------------------------------------------------------------------------
// Platform detection helpers
// ---------------------------------------------------------------------------

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as { standalone?: boolean }).standalone === true
  );
}

function isIOSSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isIOS = /iP(hone|ad|od)/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|OPiOS/.test(ua);
  return isIOS && isSafari;
}

// ---------------------------------------------------------------------------
// Dismissal state helpers
// ---------------------------------------------------------------------------

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage not available — install prompt stays silent.
  }
}

function getDismissCount(): number {
  return Math.max(
    0,
    Number(safeGetItem(PWA_STORAGE_KEYS.dismissCount) ?? "0"),
  );
}

function getLastDismissedAt(): number {
  return Number(safeGetItem(PWA_STORAGE_KEYS.installDismissed) ?? "0");
}

function isInCooldown(): boolean {
  const count = getDismissCount();
  if (count < INSTALL_MAX_DISMISSALS) return false;
  const lastTs = getLastDismissedAt();
  if (!lastTs) return false;
  const cooldownMs = INSTALL_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  return Date.now() - lastTs < cooldownMs;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/** Native browser install-prompt event (Chrome/Edge). */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export interface PWAInstallState {
  /** True when the browser fired `beforeinstallprompt` and the criteria pass. */
  canInstall: boolean;
  /** True on iOS Safari where no event fires; caller shows manual guide. */
  showIOSGuide: boolean;
  /** Call to trigger the native install prompt (Chrome/Edge). No-op on iOS. */
  prompt: () => Promise<void>;
  /** Record a user dismissal and decrement remaining chances. */
  dismiss: () => void;
  /** True when the app is running in standalone (already installed). */
  isInstalled: boolean;
}

export function usePWAInstall(): PWAInstallState {
  const [installed] = useState<boolean>(() =>
    typeof window === "undefined" ? false : isStandalone(),
  );
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  const promptRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (installed) return;
    if (isInCooldown()) return;

    // iOS Safari: no beforeinstallprompt — show guide instead.
    if (isIOSSafari() && !isStandalone()) {
      setShowIOSGuide(true);
      return;
    }

    function onBeforeInstallPrompt(e: Event) {
      e.preventDefault();
      const evt = e as BeforeInstallPromptEvent;
      promptRef.current = evt;
      setDeferredPrompt(evt);
      setCanInstall(true);
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", () => {
      setCanInstall(false);
      setDeferredPrompt(null);
      promptRef.current = null;
    });

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    };
  }, [installed]);

  const prompt = useCallback(async () => {
    const evt = promptRef.current ?? deferredPrompt;
    if (!evt) return;
    await evt.prompt();
    const choice = await evt.userChoice;
    if (choice.outcome === "accepted") {
      setCanInstall(false);
      setDeferredPrompt(null);
      promptRef.current = null;
    }
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    const prev = getDismissCount();
    const next = prev + 1;
    safeSetItem(PWA_STORAGE_KEYS.dismissCount, String(next));
    safeSetItem(PWA_STORAGE_KEYS.installDismissed, String(Date.now()));
    setCanInstall(false);
    setShowIOSGuide(false);
  }, []);

  return { canInstall, showIOSGuide, prompt, dismiss, isInstalled: installed };
}
